import React from 'react';
import PropTypes from 'prop-types';

import DragData from './DragData';
import styles from './graphicGapMatchInteraction.scss';
import GapChoice from './GapChoice';
import LoadTracker from '../../../../services/LoadTracker';
import HiddenItemFlagButton from '../../itemFlag/HiddenItemFlagButton';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import { t } from '../../../../services/i18nService';

class AssociableHotspot extends React.Component {
    static propTypes = {
        correctResponses: PropTypes.array,
        eventTracker: PropTypes.object.isRequired,
        droppedGapChoices: PropTypes.array,
        gapChoiceModels: PropTypes.array,
        handleDrop: PropTypes.func.isRequired,
        handleUserResponse: PropTypes.func.isRequired,
        handleGapChoiceArrowKeyUp: PropTypes.func.isRequired,
        handleGapChoiceDeleteKeyUp: PropTypes.func.isRequired,
        handleGapChoiceDragStart: PropTypes.func.isRequired,
        handleGapChoiceDragEnd: PropTypes.func.isRequired,
        hotspotModel: PropTypes.object.isRequired,
        isReadOnly: PropTypes.bool,
        reCalculatedGapChoiceDimensions: PropTypes.object,
        reportReady: PropTypes.func.isRequired,
        services: PropTypes.object.isRequired,
        userResponse: PropTypes.array,
        orderNumber: PropTypes.string,
        questionModel: PropTypes.object,
        model: PropTypes.object,
        isActive: PropTypes.bool,
    };

    constructor(props) {
        super(props);
        this.state = { dragOver: false };
        this.readyTracker = new LoadTracker(props.reportReady, `Gap ${props.hotspotModel.identifier}`);
        this.gapChoiceReporters = props.gapChoiceModels
            .reduce((trackers, gapChoiceModel) => {
                trackers[gapChoiceModel.identifier] = this.readyTracker.addItemToTrack(`GapChoice ${gapChoiceModel.identifier}`);
                return trackers;
            }, {});
        this.readyTracker.setAllItemsAdded();
        this.navigateToScorableItem = this.navigateToScorableItem.bind(this);
    }

    componentWillUnmount() {
        this.readyTracker.kill();
    }

    handleDragEnter = (e) => {
        if (this.props.eventTracker.isValid(e)) {
            e.preventDefault();
            this.setState({ dragOver: true });
        }
    };

    handleDragOver = (e) => {
        if (this.props.eventTracker.isValid(e)) {
            e.preventDefault();
        }
    };

    handleDragLeave = (e) => {
        e.preventDefault();
        this.setState({ dragOver: false });
    };

    handleDrop = (e) => {
        const { eventTracker, handleDrop, hotspotModel } = this.props;
        const { gapChoiceIdentifier, hotspotIdentifier: previousHotspotIdentifier } = DragData.parseFromEvent(e);
        if (gapChoiceIdentifier) {
            e.preventDefault();
            eventTracker.forget(e);
            handleDrop(gapChoiceIdentifier, hotspotModel.identifier, previousHotspotIdentifier);
            this.setState({ dragOver: false });
            this.navigateToScorableItem();
        }
    };

    navigateToScorableItem() {
        const { services, questionModel, model, hotspotModel } = this.props;
        services.scorableItemNumberingService.navigateToScorableItem(
            questionModel.contentRevisionId,
            model.identifier,
            hotspotModel.identifier,
            true
        );
    }

    handleDoubleClick = (e) => {
        const { hotspotModel, handleUserResponse } = this.props;
        const gapChoiceIdentifier = e.target.dataset.identifier;
        handleUserResponse(gapChoiceIdentifier, undefined, hotspotModel.identifier);
    };

    shouldResizeGapChoicesToHotspot = () => {
        return this.props.hotspotModel.matchMax === '1';
    };

    isDropped = gapChoiceIdentifier => this.props.droppedGapChoices.indexOf(gapChoiceIdentifier) !== -1;

    renderGapChoiceElements() {
        const { gapChoiceModels } = this.props;

        return gapChoiceModels.map((gapChoiceModel) => {
            return (
                <GapChoice
                    correctResponses={this.props.correctResponses}
                    eventTracker={this.props.eventTracker}
                    gapChoiceModel={gapChoiceModel}
                    handleUserResponse={this.props.handleUserResponse}
                    handleGapChoiceArrowKeyUp={this.props.handleGapChoiceArrowKeyUp}
                    handleGapChoiceDeleteKeyUp={this.props.handleGapChoiceDeleteKeyUp}
                    handleGapChoiceDragStart={this.props.handleGapChoiceDragStart}
                    handleGapChoiceDragEnd={this.props.handleGapChoiceDragEnd}
                    hotspotModel={this.props.hotspotModel}
                    isDropped={this.isDropped(gapChoiceModel.identifier)}
                    isReadOnly={this.props.isReadOnly}
                    key={gapChoiceModel.identifier}
                    reCalculatedGapChoiceDimensions={this.props.reCalculatedGapChoiceDimensions}
                    reportReady={this.gapChoiceReporters[gapChoiceModel.identifier]}
                    services={this.props.services}
                    shouldResizeToHotspot={this.shouldResizeGapChoicesToHotspot()}
                    userResponse={this.props.userResponse}
                />
            );
        });
    }

    gerOrderNumber() {
        const { gapChoiceModels, orderNumber } = this.props;
        if (!gapChoiceModels.length) {
            return (
                <span className="gapOrderNumber">{orderNumber}</span>
            );
        }
        return null;
    }

    render() {
        const { hotspotModel, gapChoiceModels, isActive, orderNumber, questionModel, model } = this.props;
        const { width, height, position } = hotspotModel;
        const { dragOver } = this.state;
        const isActiveClass = isActive ? ' active' : '';
        const isPopulatedClass = gapChoiceModels.length && !isActive ? ' populated' : '';
        const isDragOverClass = dragOver ? ' drag-over' : '';
        const classNames = `${styles.associableHotspot} hotspot ${isDragOverClass}${isPopulatedClass}${isActiveClass}`;
        const style = {
            left: `${position.left}px`,
            top: `${position.top}px`,
            height: `${height}px`,
            width: `${width}px`,
        };
        const addHiddenFocusableFlag = scorableItemNumberingService
            .shouldAddHiddenFlag(questionModel.contentRevisionId, model.identifier, hotspotModel.identifier);
        const isV4Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4);
        const isV2Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v2);

        // Consolidating accessibility attributes here
        const a11yAttributes = {
            'role': isV4Supported ? 'region' : 'button',
            'aria-roledescription': isV2Supported ? t('ariaRoleDescription.dropZone') : undefined,
            'tabIndex': isV4Supported ? undefined : 0,
            'onFocus': isV4Supported ? undefined : this.navigateToScorableItem,
        };

        return (
            <div
                className={classNames}
                data-type="drop"
                data-identifier={hotspotModel.identifier}
                onDragEnter={this.handleDragEnter}
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                onDrop={this.handleDrop}
                onDoubleClick={this.handleDoubleClick}
                style={style}
                id={orderNumber ? `scorableItem-${questionModel.contentRevisionId}_${orderNumber}` : undefined}
                {...a11yAttributes}
            >
                {this.gerOrderNumber()}
                {
                    this.renderGapChoiceElements()
                }
                {
                    addHiddenFocusableFlag && <HiddenItemFlagButton
                        orderNumber={orderNumber}
                        responseIdentifier={hotspotModel.identifier}
                        contentRevisionId={questionModel.contentRevisionId}
                        choiceIdentifier={hotspotModel.identifier}
                    />
                }
            </div>
        );
    }
}

export default AssociableHotspot;
